<script lang="ts">
	// UC-18, the owner's half: find a customer the till saved, correct their
	// name, edit or take away an address, or delete them when they ask to be
	// forgotten. The owner never adds one here; the till does, each time a
	// delivery's details are finished.
	//
	// The search asks the server as the owner types: digits find a phone,
	// anything else a name, and an empty search lists the most recent. One
	// customer opens in a Sheet. Deleting is undone, not confirmed: the row
	// leaves at once and the server hears of it when the Undo runs out.
	// Their past orders keep what they were placed with either way.
	//
	// An address is edited in place, one at a time. It keeps the shop it was
	// last used from, so its areas are that shop's, read when Edit is tapped.

	import AddressEdit from './AddressEdit.svelte';
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Sheet from '../components/Sheet.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { api } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	import { formatWhen } from '../format/time';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import type { AddressEdit as AddressChange, AddressEditOutcome } from '../till/customers.svelte';
	import type { components } from '../api/types';
	import { untrack } from 'svelte';

	type Customer = components['schemas']['CustomerBody'];
	type Area = components['schemas']['AreaBody'];

	interface Props {
		language: Language;
		minorDigits: number;
	}

	let { language, minorDigits }: Props = $props();

	// A search waits for a short pause in the typing rather than asking on
	// every key.
	const SEARCH_PAUSE_MS = 250;

	let query = $state('');
	let rows = $state<Customer[]>([]);
	let loaded = $state(false);
	let failure = $state('');
	// The last answer to an empty search had nobody: there is nobody at all.
	let nobody = $state(false);

	let open = $state(false);
	let person = $state<Customer | null>(null);
	let name = $state('');
	let saving = $state(false);
	// The address open for editing, and its shop's areas.
	let editing = $state<{ id: string; areas: Area[] } | null>(null);
	let sheetFailure = $state('');

	let asked = 0;

	async function load(q: string) {
		const ticket = ++asked;
		try {
			const { data, error } = await api.GET('/api/owner/customers', {
				params: { query: q ? { q } : {} },
			});
			if (ticket !== asked) return;
			if (error !== undefined || !data) {
				failure = message(language, 'loadFailed');
				return;
			}
			failure = '';
			rows = data.customers ?? [];
			if (!q) nobody = rows.length === 0;
		} catch {
			if (ticket === asked) failure = message(language, 'loadFailed');
		} finally {
			if (ticket === asked) loaded = true;
		}
	}

	$effect(() => {
		const q = query.trim();
		// The first read goes at once; only typing waits for the pause.
		const pause = untrack(() => loaded) ? SEARCH_PAUSE_MS : 0;
		const timer = setTimeout(() => void load(q), pause);
		return () => clearTimeout(timer);
	});

	function refresh() {
		void load(query.trim());
	}

	function when(iso: string | undefined): string {
		return (
			formatWhen(iso, language, {
				today: message(language, 'whenToday'),
				yesterday: message(language, 'whenYesterday'),
			}) || '—'
		);
	}

	// Someone else deleted them meanwhile: say so and show the list as it is.
	function gone() {
		open = false;
		flash(message(language, 'customerGone'));
		refresh();
	}

	async function openCustomer(row: Customer) {
		person = row;
		name = row.name;
		sheetFailure = '';
		editing = null;
		open = true;
		try {
			const { data, error, response } = await api.GET('/api/owner/customers/{customerId}', {
				params: { path: { customerId: row.id } },
			});
			if (response.status === 404) return gone();
			if (error !== undefined || !data || person?.id !== row.id) return;
			person = data;
		} catch {
			// The row's own figures are showing; the addresses wait for a retry.
			sheetFailure = message(language, 'loadFailed');
		}
	}

	const trimmed = $derived(name.trim());

	async function save() {
		if (!person || !trimmed || trimmed === person.name || saving) return;
		saving = true;
		sheetFailure = '';
		try {
			const { response } = await api.PATCH('/api/owner/customers/{customerId}', {
				params: { path: { customerId: person.id } },
				body: { name: trimmed },
			});
			if (response.status === 404) return gone();
			if (!response.ok) {
				sheetFailure = message(language, 'loadFailed');
				return;
			}
			open = false;
			flash(message(language, 'saved'));
			refresh();
		} catch {
			sheetFailure = message(language, 'loadFailed');
		} finally {
			saving = false;
		}
	}

	async function startEdit(addressId: string, shopId: string | undefined) {
		sheetFailure = '';
		if (!shopId) {
			editing = { id: addressId, areas: [] };
			return;
		}
		try {
			const { data, error } = await api.GET('/api/owner/shops/{shopId}/areas', {
				params: { path: { shopId } },
			});
			if (error !== undefined || !data) {
				sheetFailure = message(language, 'loadFailed');
				return;
			}
			editing = { id: addressId, areas: data.areas ?? [] };
		} catch {
			sheetFailure = message(language, 'loadFailed');
		}
	}

	async function saveAddress(
		addressId: string,
		change: AddressChange,
	): Promise<AddressEditOutcome> {
		if (!person) return 'failed';
		sheetFailure = '';
		try {
			const { response, error } = await api.PATCH(
				'/api/owner/customers/{customerId}/addresses/{addressId}',
				{
					params: { path: { customerId: person.id, addressId } },
					body: {
						address: change.address.trim(),
						...(change.landmark.trim() ? { landmark: change.landmark.trim() } : {}),
						...(change.deliveryAreaId ? { deliveryAreaId: change.deliveryAreaId } : {}),
					},
				},
			);
			if (response.status === 422 && error?.details?.path === 'address') return 'taken';
			if (response.status === 404) {
				editing = null;
				await openCustomer(person);
				return 'gone';
			}
			if (!response.ok) {
				sheetFailure = message(language, 'loadFailed');
				return 'failed';
			}
			editing = null;
			await openCustomer(person);
			return 'saved';
		} catch {
			sheetFailure = message(language, 'loadFailed');
			return 'failed';
		}
	}

	// The address leaves the list at once, and the server hears of it when
	// the Undo runs out. A failure puts it back.
	function removeAddress(addressId: string) {
		if (!person) return;
		const owner = person;
		const list = owner.addresses ?? [];
		const at = list.findIndex((a) => a.id === addressId);
		const address = list[at];
		if (!address) return;
		editing = null;
		sheetFailure = '';
		person = { ...owner, addresses: list.filter((a) => a.id !== addressId) };
		const putBack = () => {
			if (person?.id !== owner.id) return;
			const now = [...(person.addresses ?? [])];
			now.splice(Math.min(at, now.length), 0, address);
			person = { ...person, addresses: now };
		};
		removeWithUndo(
			`${message(language, 'customerAddressRemoved')} ${address.address}`,
			async () => {
				try {
					const { response } = await api.POST(
						'/api/owner/customers/{customerId}/addresses/{addressId}/remove',
						{ params: { path: { customerId: owner.id, addressId } } },
					);
					if (response.ok) return;
					// An address already gone is gone either way; a customer gone
					// is not, and reading them again says which.
					if (response.status === 404) {
						if (person?.id === owner.id) await openCustomer(owner);
						return;
					}
					putBack();
					if (person?.id === owner.id) sheetFailure = message(language, 'loadFailed');
				} catch {
					putBack();
					if (person?.id === owner.id) sheetFailure = message(language, 'loadFailed');
				}
			},
			putBack,
		);
	}

	function remove() {
		if (!person) return;
		const target = person;
		const at = rows.findIndex((row) => row.id === target.id);
		rows = rows.filter((row) => row.id !== target.id);
		open = false;
		removeWithUndo(
			`${message(language, 'customersDeleted')} ${target.name || target.phone}`,
			async () => {
				try {
					await api.POST('/api/owner/customers/{customerId}/remove', {
						params: { path: { customerId: target.id } },
					});
				} finally {
					refresh();
				}
			},
			() => {
				if (at >= 0) rows.splice(at, 0, target);
			},
		);
	}

	const columns = $derived([
		{ label: message(language, 'customersName'), w: 'minmax(0,1.3fr)' },
		{ label: message(language, 'customersPhone'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'customersOrders'), w: '80px', align: 'end' as const },
		{ label: message(language, 'customersLast'), w: 'minmax(0,1fr)' },
		{ label: '', w: '80px' },
	]);
</script>

<div class="customers" data-testid="panel-customers">
	{#if !loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'customersLoading')}
				caption={message(language, 'customersLoading')}
			/>
		</div>
	{:else}
		{#if failure}
			<Band tone="flag" testId="customers-error">{failure}</Band>
		{/if}
		{#if nobody && !query.trim()}
			<Card pad>
				<p class="empty" data-testid="customers-empty">{message(language, 'customersEmpty')}</p>
			</Card>
		{:else}
			<div class="search">
				<Field
					testId="customers-search"
					label={message(language, 'customersSearch')}
					value={query}
					maxLength={120}
					onInput={(value) => (query = value)}
				/>
			</div>
			{#if rows.length === 0}
				<Card pad>
					<p class="empty" data-testid="customers-no-match">
						{message(language, 'customersNoMatch')}
					</p>
				</Card>
			{:else}
				<TablePanel
					{columns}
					title={message(language, 'ownerCustomers')}
					note={message(language, 'customersListNote')}
					testId="customers-list"
				>
					{#each rows as row (row.id)}
						<Row testId="customer-{row.id}">
							{#if row.name}
								<span class="name">{row.name}</span>
							{:else}
								<span class="unnamed">{message(language, 'customersNoName')}</span>
							{/if}
							<bdi class="n soft" dir="ltr">{row.phone}</bdi>
							<span class="n strong end" dir="ltr">{row.orders}</span>
							<span class="soft">{when(row.lastOrderAt)}</span>
							<span class="actions">
								<Button
									testId="customer-open-{row.id}"
									label={message(language, 'customersOpen')}
									tone="text"
									size="sm"
									onPress={() => void openCustomer(row)}
								/>
							</span>
						</Row>
					{/each}
				</TablePanel>
			{/if}
		{/if}
	{/if}
</div>

<Sheet
	{open}
	title={person ? person.name || person.phone : ''}
	titleTestId="customer-title"
	primaryLabel={message(language, 'customerSaveName')}
	primaryTestId="customer-save"
	primaryDisabled={!person || !trimmed || trimmed === person.name}
	primaryState={saving ? 'pending' : 'idle'}
	pendingLabel={message(language, 'saving')}
	onPrimary={() => void save()}
	secondaryLabel={message(language, 'customersDelete')}
	secondaryTestId="customer-delete"
	onSecondary={remove}
	onClose={() => (open = false)}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="customer-close"
	testId="customer-sheet"
>
	{#if person}
		<div class="sheet" data-scroll="">
			{#if sheetFailure}
				<Band tone="flag" testId="customer-error">{sheetFailure}</Band>
			{/if}
			<Field
				testId="customer-name"
				label={message(language, 'customersName')}
				value={name}
				maxLength={120}
				error={trimmed ? undefined : message(language, 'customerNameRequired')}
				onInput={(value) => (name = value)}
			/>
			<div class="facts">
				<div class="fact">
					<span class="label">{message(language, 'customersPhone')}</span>
					<bdi class="n value" dir="ltr">{person.phone}</bdi>
				</div>
				<div class="fact">
					<span class="label">{message(language, 'customersOrders')}</span>
					<bdi class="n value" dir="ltr">{person.orders}</bdi>
				</div>
				<div class="fact">
					<span class="label">{message(language, 'customersLast')}</span>
					<span class="value words">{when(person.lastOrderAt)}</span>
				</div>
			</div>
			<div class="saved">
				<span class="heading">{message(language, 'customerAddresses')}</span>
				{#if (person.addresses ?? []).length === 0}
					<p class="none" data-testid="customer-no-addresses">
						{message(language, 'customersNoAddresses')}
					</p>
				{:else}
					<div class="list">
						{#each person.addresses ?? [] as address (address.id)}
							{@const off = editing !== null && editing.id !== address.id}
							<div class="address" data-testid="customer-address-{address.id}">
								{#if editing?.id === address.id}
									<AddressEdit
										{language}
										{minorDigits}
										testId="customer-address-edit"
										address={address.address}
										landmark={address.landmark ?? ''}
										areaId={address.areaId ?? ''}
										areas={editing.areas}
										areasTitle={fill(message(language, 'customerAreaOf'), address.shopName ?? '')}
										size="sm"
										onCancel={() => (editing = null)}
										onSave={(change) => saveAddress(address.id, change)}
									/>
								{:else}
									<div class="view">
										<span class="text">
											<span class="line">{address.address}</span>
											{#if address.landmark}
												<span class="landmark">{address.landmark}</span>
											{/if}
											<span class="where">
												{[address.shopName, address.areaName || message(language, 'deliveryNoArea')]
													.filter(Boolean)
													.join(' · ')}
											</span>
										</span>
										<Button
											testId="customer-address-edit-{address.id}"
											label={message(language, 'customerEditAddress')}
											tone="text"
											size="sm"
											disabled={off}
											onPress={() => void startEdit(address.id, address.shopId)}
										/>
										<Button
											testId="customer-address-remove-{address.id}"
											label={message(language, 'remove')}
											tone="text-quiet"
											size="sm"
											disabled={off}
											onPress={() => removeAddress(address.id)}
										/>
									</div>
								{/if}
							</div>
						{/each}
					</div>
				{/if}
			</div>
			<p class="note">{message(language, 'customersDeleteNote')}</p>
		</div>
	{/if}
</Sheet>

<style>
	.customers {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.search {
		max-inline-size: 420px;
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.name {
		min-inline-size: 0;
		font-weight: 600;
	}

	.unnamed {
		min-inline-size: 0;
		color: var(--text-muted);
	}

	.n {
		font-family: var(--font-mono);
	}

	.soft {
		min-inline-size: 0;
		color: var(--text-body);
		white-space: nowrap;
	}

	.strong {
		font-weight: 700;
	}

	.end {
		text-align: end;
	}

	.actions {
		min-inline-size: 0;
		display: flex;
		justify-content: flex-end;
	}

	.sheet {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.facts {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3) var(--space-9);
	}

	.fact {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.value {
		font-size: var(--text-sm);
		font-weight: 700;
		color: var(--text-strong);
	}

	.value.words {
		font-weight: 600;
	}

	.saved {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.heading {
		font-size: var(--text-base);
		font-weight: 600;
		color: var(--text-strong);
	}

	.none {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.list {
		display: flex;
		flex-direction: column;
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
		background: var(--surface-card);
	}

	.address {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
		padding: var(--space-4) var(--space-5);
	}

	.view {
		display: flex;
		align-items: center;
		gap: var(--space-4);
	}

	.address + .address {
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.text {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.line {
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
		text-wrap: pretty;
	}

	.landmark {
		font-size: var(--text-xs);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.where {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.note {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}
</style>
